import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  X,
  Loader,
  AlertCircle,
  ExternalLink,
  User,
  Users,
  Package,
  Handshake,
  Tag,
  Pencil,
  Trash2,
  Check,
  Globe,
  UsersRound,
} from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { partnershipsApiService } from '../../services/partnershipsApi';
import {
  CreatorFull,
  PartnershipCreatorCode,
  UpdateCreatorRequest,
} from '../../types/partnership';
import { PartnershipStatusBadge } from './PartnershipStatusBadge';
import { PartnershipPriorityBadge } from './PartnershipPriorityBadge';
import { cn } from '../../utils/cn';
import { formatDistanceToNow } from 'date-fns';
import { ConfirmationModal } from '../ConfirmationModal';
import { partnerPagePath } from '../../utils/partnerLinks';

interface CreatorDetailModalProps {
  creatorId: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  /** Hide "Open partner page" when the modal is opened from that page. */
  hidePartnerLink?: boolean;
  /** Called after the creator is deleted, once the modal has closed. */
  onDeleted?: () => void;
}

export const CreatorDetailModal = ({
  creatorId,
  isOpen,
  onClose,
  onUpdate,
  canEdit = false,
  canDelete = false,
  hidePartnerLink = false,
  onDeleted,
}: CreatorDetailModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [creator, setCreator] = useState<CreatorFull | null>(null);
  const [codes, setCodes] = useState<PartnershipCreatorCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Edit state
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState<'individual' | 'team'>('individual');
  const [editContact, setEditContact] = useState('');
  const [editWebsite, setEditWebsite] = useState('');
  const [editNotes, setEditNotes] = useState('');

  useEffect(() => {
    if (!isOpen || !creatorId) return;
    setLoading(true);
    setError(null);

    Promise.all([
      partnershipsApiService.getCreatorFull(creatorId),
      partnershipsApiService.getCreatorCodes(creatorId),
    ])
      .then(([creatorData, codesData]) => {
        setCreator(creatorData);
        setCodes(codesData);
        // Pre-fill edit fields
        setEditName(creatorData.name);
        setEditType(creatorData.type);
        setEditContact(creatorData.contact_info || '');
        setEditWebsite(creatorData.website || '');
        setEditNotes(creatorData.notes || '');
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load creator'))
      .finally(() => setLoading(false));
  }, [isOpen, creatorId]);

  const handleSave = async () => {
    if (!creator) return;
    setIsSaving(true);
    try {
      const data: UpdateCreatorRequest = {
        name: editName.trim(),
        type: editType,
        contact_info: editContact.trim() || null,
        website: editWebsite.trim() || null,
        notes: editNotes.trim() || null,
      };
      await partnershipsApiService.updateCreator(creator.id, data);
      // Reload
      const updated = await partnershipsApiService.getCreatorFull(creator.id);
      setCreator(updated);
      setIsEditing(false);
      onUpdate?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update creator');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!creator) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await partnershipsApiService.deleteCreator(creator.id);
      onUpdate?.();
      onClose();
      onDeleted?.();
    } catch (err) {
      // Argus refuses (409) while Mr. Roboto manages this partner's channel:
      // say so here rather than in a browser dialog.
      setDeleteError(err instanceof Error ? err.message : 'The creator could not be deleted.');
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  const parsePlatformLinks = (links: string | null): Record<string, string> => {
    if (!links) return {};
    try { return JSON.parse(links); } catch { return {}; }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className="modal-panel max-w-2xl">
        <button onClick={onClose} className="absolute top-3 right-3 text-glass-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-6 h-6 animate-spin text-accent-400" />
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        {creator && !loading && (
          <div className="space-y-5">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div>
                {isEditing ? (
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="glass-input text-lg font-bold mb-1"
                  />
                ) : (
                  <h2 className="text-lg font-semibold tracking-tight text-white mb-1">{creator.name}</h2>
                )}
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <div className="flex gap-2">
                      {(['individual', 'team'] as const).map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setEditType(t)}
                          className={cn(
                            'glass-button-secondary text-xs py-1 px-2 flex items-center gap-1',
                            editType === t && 'bg-accent-500/15 text-accent-300 border-accent-500/25'
                          )}
                        >
                          {t === 'individual' ? <User className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                          {t}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-sm text-glass-400">
                      {creator.type === 'individual' ? <User className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                      {creator.type}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-wrap justify-end">
                {!hidePartnerLink && !isEditing && (
                  <Link
                    to={partnerPagePath(creator.id)}
                    className="glass-button-secondary text-xs py-1 px-2"
                    title="The team, their Discord channel, update reports and server packs"
                  >
                    <UsersRound className="w-3 h-3 mr-1" />
                    Open partner page
                  </Link>
                )}
                {canEdit && !isEditing && (
                  <button onClick={() => setIsEditing(true)} className="glass-button-secondary text-xs py-1 px-2">
                    <Pencil className="w-3 h-3 mr-1" />Edit
                  </button>
                )}
                {isEditing && (
                  <>
                    <button onClick={handleSave} disabled={isSaving} className="glass-button text-xs py-1 px-2">
                      {isSaving ? <Loader className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                      Save
                    </button>
                    <button onClick={() => setIsEditing(false)} className="glass-button-secondary text-xs py-1 px-2">
                      Cancel
                    </button>
                  </>
                )}
                {canDelete && (
                  <button
                    onClick={() => setIsDeleteOpen(true)}
                    className="glass-button-secondary text-xs py-1 px-2 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {deleteError && (
              <div className="alert alert-danger" role="alert">
                <AlertCircle className="w-4 h-4" />
                <span className="flex-1">{deleteError}</span>
                <button type="button" onClick={() => setDeleteError(null)} className="btn-ghost btn-xs ml-auto shrink-0">Dismiss</button>
              </div>
            )}

            {/* Contact / Website / Notes */}
            <div className="space-y-3">
              {isEditing ? (
                <>
                  <div>
                    <label className="text-xs text-glass-500 mb-1 block">Contact Info</label>
                    <textarea
                      value={editContact}
                      onChange={(e) => setEditContact(e.target.value)}
                      className="glass-input w-full text-sm"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-glass-500 mb-1 block">Website</label>
                    <input
                      value={editWebsite}
                      onChange={(e) => setEditWebsite(e.target.value)}
                      className="glass-input w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-glass-500 mb-1 block">Notes</label>
                    <textarea
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      className="glass-input w-full text-sm"
                      rows={3}
                    />
                  </div>
                </>
              ) : (
                <>
                  {creator.contact_info && (
                    <div>
                      <span className="text-xs text-glass-500 block mb-0.5">Contact</span>
                      <p className="text-sm text-glass-200 whitespace-pre-wrap">{creator.contact_info}</p>
                    </div>
                  )}
                  {creator.website && (
                    <div>
                      <span className="text-xs text-glass-500 block mb-0.5">Website</span>
                      <a href={creator.website} target="_blank" rel="noopener noreferrer" className="text-sm text-accent-400 hover:text-accent-300 inline-flex items-center gap-1">
                        <Globe className="w-3 h-3" />{creator.website}
                      </a>
                    </div>
                  )}
                  {creator.notes && (
                    <div>
                      <span className="text-xs text-glass-500 block mb-0.5">Notes</span>
                      <p className="text-sm text-glass-200 whitespace-pre-wrap">{creator.notes}</p>
                    </div>
                  )}
                </>
              )}

              {/* Platform Links */}
              {!isEditing && (() => {
                const links = parsePlatformLinks(creator.platform_links);
                const entries = Object.entries(links);
                if (entries.length === 0) return null;
                return (
                  <div>
                    <span className="text-xs text-glass-500 block mb-1">Platforms</span>
                    <div className="flex flex-wrap gap-2">
                      {entries.map(([platform, url]) => (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-glass-100/10 text-xs text-accent-400 hover:text-accent-300 hover:bg-glass-200/15 border border-glass-200/10"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {platform}
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Modpacks */}
            <div>
              <h3 className="text-sm font-semibold text-glass-300 mb-2 flex items-center gap-1.5">
                <Package className="w-4 h-4" />
                Modpacks ({creator.modpacks?.length || 0})
              </h3>
              {creator.modpacks && creator.modpacks.length > 0 ? (
                <div className="space-y-2">
                  {creator.modpacks.map((mp) => (
                    <div key={mp.id} className="p-3 rounded-lg bg-dark-900/40 border border-glass-200/8">
                      <div className="flex items-center gap-3">
                        {mp.logo_url && (
                          <img src={mp.logo_url} alt="" className="w-8 h-8 rounded-md object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-white block truncate">{mp.name}</span>
                          {mp.platform_type && (
                            <span className="text-[11px] text-glass-400">{mp.platform_type}</span>
                          )}
                        </div>
                        {mp.player_count_estimate && (
                          <span className="text-xs text-glass-400">{mp.player_count_estimate.toLocaleString()} players</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-glass-500">No modpacks</p>
              )}
            </div>

            {/* Partnerships */}
            <div>
              <h3 className="text-sm font-semibold text-glass-300 mb-2 flex items-center gap-1.5">
                <Handshake className="w-4 h-4" />
                Partnerships ({creator.partnerships?.length || 0})
              </h3>
              {creator.partnerships && creator.partnerships.length > 0 ? (
                <div className="space-y-2">
                  {creator.partnerships.map((p) => (
                    <div key={p.id} className="p-3 rounded-lg bg-dark-900/40 border border-glass-200/8 flex items-center gap-3">
                      <PartnershipStatusBadge status={p.status} />
                      <PartnershipPriorityBadge priority={p.priority} />
                      <span className="text-sm text-glass-200 flex-1 truncate">{p.summary || 'No summary'}</span>
                      <span className="text-[11px] text-glass-500">
                        {formatDistanceToNow(new Date(p.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-glass-500">No partnerships</p>
              )}
            </div>

            {/* Creator Codes */}
            {codes.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-glass-300 mb-2 flex items-center gap-1.5">
                  <Tag className="w-4 h-4" />
                  Creator Codes ({codes.length})
                </h3>
                <div className="space-y-2">
                  {codes.map((c) => (
                    <div key={c.id} className="p-2 rounded-lg bg-dark-900/40 border border-glass-200/8 flex items-center gap-3 text-sm">
                      <code className="font-mono text-accent-300 bg-accent-500/10 px-2 py-0.5 rounded">{c.code}</code>
                      <span className="text-glass-400">{c.discount_percent}% off</span>
                      <span className="text-glass-400">{c.revenue_share_percent}% rev share</span>
                      <span className={cn(
                        'text-[11px] px-1.5 py-0.5 rounded-full',
                        c.status === 'active' && 'bg-green-500/15 text-green-300',
                        c.status === 'expired' && 'bg-yellow-500/15 text-yellow-300',
                        c.status === 'revoked' && 'bg-red-500/15 text-red-300',
                      )}>{c.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata footer */}
            <div className="text-[11px] text-glass-500 pt-3 border-t border-glass-200/10">
              Created {formatDistanceToNow(new Date(creator.created_at), { addSuffix: true })}
              {' · '}ID: <code className="font-mono">{creator.id}</code>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {isDeleteOpen && (
          <ConfirmationModal
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            onConfirm={handleDelete}
            title="Delete Creator"
            message="Are you sure? This will also delete all their modpacks and partnerships (cascade). This cannot be undone."
            confirmText="Delete"
            isLoading={isDeleting}
          />
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
